import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

// ── Super Admin Dashboard — /superadmin ───────────────────────────────────────
// Only accessible to users with role = 'superadmin' or 'admin'
// Track users, manage plans, configure pricing, send offers

const FONT = "'Inter',-apple-system,BlinkMacSystemFont,sans-serif";
const P    = '#2B1DC9';
const PLAN_COLORS = { hobby:'#64748B', solopreneur:'#7C3AED', agency:'#2B1DC9' };

function useAdmin() {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) { setLoading(false); return; }
      setUser(session.user);
      supabase.from('profiles').select('role').eq('id', session.user.id).maybeSingle()
        .then(({ data }) => {
          setIsAdmin(data?.role === 'admin' || data?.role === 'superadmin');
          setLoading(false);
        });
    });
  }, []);

  return { user, loading, isAdmin };
}

export default function SuperAdminPage() {
  const { user, loading, isAdmin } = useAdmin();
  const [tab, setTab] = useState('overview');

  if (loading) return <div style={{fontFamily:FONT,display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',color:'#8888AA'}}>Loading…</div>;

  if (!user) return (
    <div style={{fontFamily:FONT,display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh'}}>
      <div style={{textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:12}}>🔐</div>
        <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Admin Access Required</div>
        <a href="/" style={{color:P}}>← Back to Aspreo</a>
      </div>
    </div>
  );

  if (!isAdmin) return (
    <div style={{fontFamily:FONT,display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh'}}>
      <div style={{textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:12}}>🚫</div>
        <div style={{fontSize:18,fontWeight:700,marginBottom:8}}>Not Authorized</div>
        <div style={{color:'#8888AA',marginBottom:16}}>You don't have admin access.</div>
        <a href="/" style={{color:P}}>← Back to Aspreo</a>
      </div>
    </div>
  );

  const TABS = [
    { id:'overview',  label:'📊 Overview'    },
    { id:'users',     label:'👥 Users'       },
    { id:'plans',     label:'💳 Plans'       },
    { id:'features',  label:'⚙️ Features'    },
    { id:'pricing',   label:'🏷 Pricing Page' },
    { id:'logs',      label:'📋 Audit Logs'  },
  ];

  return (
    <div style={{fontFamily:FONT,minHeight:'100vh',background:'#F6F6F8'}}>
      {/* Header */}
      <div style={{background:'#0F0E1A',borderBottom:'1px solid #1E1E2E',padding:'14px 24px',display:'flex',alignItems:'center',gap:16}}>
        <div style={{width:32,height:32,borderRadius:8,background:P,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:900,color:'#fff',fontSize:14}}>A</div>
        <div style={{fontWeight:800,fontSize:16,color:'#fff'}}>Aspreo SuperAdmin</div>
        <div style={{marginLeft:'auto',fontSize:12,color:'#8888AA'}}>{user.email}</div>
        <a href="/" style={{fontSize:12,color:'#8888AA',textDecoration:'none'}}>← App</a>
      </div>

      <div style={{display:'flex',minHeight:'calc(100vh - 61px)'}}>
        {/* Sidebar */}
        <div style={{width:200,background:'#fff',borderRight:'1px solid #E4E4EC',padding:'16px 0',flexShrink:0}}>
          {TABS.map(t=>(
            <div key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:'10px 20px',cursor:'pointer',fontSize:13,fontWeight:tab===t.id?700:400,color:tab===t.id?P:'#4A4A6A',background:tab===t.id?P+'10':'transparent',borderLeft:tab===t.id?`3px solid ${P}`:'3px solid transparent',transition:'all .15s'}}>
              {t.label}
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{flex:1,padding:28,overflowY:'auto'}}>
          {tab==='overview'  && <AdminOverview/>}
          {tab==='users'     && <AdminUsers/>}
          {tab==='plans'     && <AdminPlans/>}
          {tab==='features'  && <AdminFeatureFlags/>}
          {tab==='pricing'   && <AdminPricingPage/>}
          {tab==='logs'      && <AdminLogs/>}
        </div>
      </div>
    </div>
  );
}

// ── OVERVIEW ──────────────────────────────────────────────────────────────────
function AdminOverview() {
  const [stats, setStats] = useState({ total:0, hobby:0, solopreneur:0, agency:0, today:0, week:0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: up } = await supabase.from('user_plans').select('plan_id,created_at,status');
      const { data: pr } = await supabase.from('profiles').select('created_at');
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const weekStart  = new Date(now.getTime() - 7*86400000).toISOString();
      setStats({
        total:       pr?.length || 0,
        hobby:       (up||[]).filter(u=>u.plan_id==='hobby').length,
        solopreneur: (up||[]).filter(u=>u.plan_id==='solopreneur').length,
        agency:      (up||[]).filter(u=>u.plan_id==='agency').length,
        today:       (pr||[]).filter(u=>u.created_at>=todayStart).length,
        week:        (pr||[]).filter(u=>u.created_at>=weekStart).length,
      });
      setLoading(false);
    })();
  }, []);

  const KPIS = [
    {label:'Total Users',       value:stats.total,       icon:'👥', color:P},
    {label:'Hobby (Free)',       value:stats.hobby,       icon:'🆓', color:'#64748B'},
    {label:'Solopreneur (₹499)', value:stats.solopreneur, icon:'⭐', color:'#7C3AED'},
    {label:'Agency (₹999)',      value:stats.agency,      icon:'🏢', color:'#2B1DC9'},
    {label:'Joined Today',       value:stats.today,       icon:'🌅', color:'#16A34A'},
    {label:'Joined This Week',   value:stats.week,        icon:'📅', color:'#D97706'},
  ];

  const MRR = stats.solopreneur*499 + stats.agency*999;

  return (
    <div>
      <h2 style={{margin:'0 0 20px',fontSize:22,fontWeight:800,color:'#0F0E1A'}}>Overview</h2>
      <div style={{background:'linear-gradient(135deg,#2B1DC9,#4F46E5)',borderRadius:14,padding:24,color:'#fff',marginBottom:20}}>
        <div style={{fontSize:13,opacity:.8,marginBottom:4}}>Monthly Recurring Revenue (Est.)</div>
        <div style={{fontSize:40,fontWeight:900}}>₹{MRR.toLocaleString('en-IN')}</div>
        <div style={{fontSize:12,opacity:.6,marginTop:4}}>{stats.solopreneur} Solopreneur × ₹499 + {stats.agency} Agency × ₹999</div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
        {KPIS.map(k=>(
          <div key={k.label} style={{background:'#fff',border:'1px solid #E4E4EC',borderRadius:10,padding:'14px 16px'}}>
            <div style={{fontSize:24,marginBottom:6}}>{k.icon}</div>
            <div style={{fontSize:28,fontWeight:800,color:k.color}}>{loading?'…':k.value}</div>
            <div style={{fontSize:11,color:'#8888AA',marginTop:2}}>{k.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── USERS ─────────────────────────────────────────────────────────────────────
function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    // Fetch ALL profiles first (so users without a plan row still show)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id,email,full_name,avatar_url,role,created_at')
      .order('created_at', { ascending:false });

    // Fetch all user_plans
    const { data: plans } = await supabase
      .from('user_plans')
      .select('*,plans(name,price_monthly)');

    const planMap = {};
    (plans||[]).forEach(p => { planMap[p.user_id] = p; });

    // Merge: every profile gets a plan row (default hobby if missing)
    const merged = (profiles||[]).map(profile => ({
      ...(planMap[profile.id] || { plan_id:'hobby', status:'active', granted_by_admin:false }),
      profiles: profile,
      plans: planMap[profile.id]?.plans || { name:'Hobby', price_monthly:0 },
    }));

    setUsers(merged);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const changePlan = async (userId, planId, note) => {
    setSaving(userId);
    // Upsert the user_plans row
    await supabase.from('user_plans').upsert({
      user_id: userId, plan_id: planId, status:'active',
      granted_by_admin:true, admin_note:note||'Admin override',
      updated_at: new Date().toISOString(),
      current_period_start: new Date().toISOString(),
    }, { onConflict:'user_id' });
    // Send welcome/change email via edge function (non-blocking)
    fetch('/api/sb/functions/v1/handle-plan-purchase', {
      method:'POST',
      headers:{'Content-Type':'application/json','Authorization':'Bearer '+process.env.REACT_APP_SUPABASE_ANON_KEY},
      body: JSON.stringify({ user_id:userId, plan_id:planId, is_admin_grant:true }),
    }).catch(()=>{});
    // Log the action
    supabase.from('admin_logs').insert({
      action:'change_plan', target_type:'user', target_id:userId,
      details:{ plan_id:planId, note, changed_by:'admin' },
    }).then(()=>{}).catch(()=>{}); // non-blocking, ignore RLS errors
    await load();
    setSaving(null);
  };

  const filtered = users.filter(u => {
    const email = u.profiles?.email || '';
    const name  = u.profiles?.full_name || '';
    const matchSearch = !search || email.includes(search.toLowerCase()) || name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter==='all' || u.plan_id===filter;
    return matchSearch && matchFilter;
  });

  return (
    <div>
      <h2 style={{margin:'0 0 16px',fontSize:22,fontWeight:800,color:'#0F0E1A'}}>Users ({users.length})</h2>
      <div style={{display:'flex',gap:10,marginBottom:14}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by email or name…"
          style={{flex:1,padding:'9px 14px',border:'1.5px solid #E4E4EC',borderRadius:9,fontFamily:FONT,fontSize:13,outline:'none'}}/>
        <select value={filter} onChange={e=>setFilter(e.target.value)}
          style={{padding:'9px 14px',border:'1.5px solid #E4E4EC',borderRadius:9,fontFamily:FONT,fontSize:13,outline:'none',cursor:'pointer'}}>
          <option value="all">All Plans</option>
          <option value="hobby">Hobby</option>
          <option value="solopreneur">Solopreneur</option>
          <option value="agency">Agency</option>
        </select>
      </div>

      {loading && <div style={{textAlign:'center',color:'#8888AA',padding:40}}>Loading users…</div>}

      <div style={{background:'#fff',border:'1px solid #E4E4EC',borderRadius:12,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{background:'#F6F6F8',borderBottom:'1px solid #E4E4EC'}}>
              {['User','Plan','Source','Joined','Status','Actions'].map(h=>(
                <th key={h} style={{padding:'10px 16px',textAlign:'left',fontWeight:700,fontSize:11,color:'#8888AA',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((u,i)=>{
              const profile = u.profiles || {};
              const planColor = PLAN_COLORS[u.plan_id] || '#64748B';
              return (
                <tr key={u.id} style={{borderBottom:'1px solid #F0F0F8',background:i%2?'#FAFAFA':'#fff'}}>
                  <td style={{padding:'12px 16px'}}>
                    <div style={{fontWeight:600,color:'#0F0E1A'}}>{profile.full_name||'—'}</div>
                    <div style={{fontSize:11,color:'#8888AA'}}>{profile.email}</div>
                  </td>
                  <td style={{padding:'12px 16px'}}>
                    <span style={{fontSize:11,padding:'2px 10px',borderRadius:99,background:planColor+'18',color:planColor,fontWeight:700}}>
                      {u.plan_id}
                    </span>
                    {u.granted_by_admin&&<span style={{fontSize:9,color:'#D97706',marginLeft:5}}>admin</span>}
                  </td>
                  <td style={{padding:'12px 16px'}}>
                    {u.granted_by_admin
                      ? <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:'#FEF9C3',color:'#D97706',fontWeight:700}}>👑 Admin Grant</span>
                      : u.plan_id!=='hobby'
                        ? <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:'#DCFCE7',color:'#16A34A',fontWeight:700}}>💳 Purchased</span>
                        : <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:'#F0F0F5',color:'#8888AA',fontWeight:700}}>🆓 Organic</span>
                    }
                  </td>
                  <td style={{padding:'12px 16px',color:'#4A4A6A',fontSize:12}}>
                    {profile.created_at?new Date(profile.created_at).toLocaleDateString('en-IN'):'—'}
                  </td>
                  <td style={{padding:'12px 16px'}}>
                    <span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:u.status==='active'?'#DCFCE7':'#FEF2F2',color:u.status==='active'?'#16A34A':'#DC2626',fontWeight:600}}>
                      {u.status}
                    </span>
                  </td>
                  <td style={{padding:'12px 16px'}}>
                    <div style={{display:'flex',gap:4,alignItems:'center'}}>
                      <select defaultValue={u.plan_id} onChange={e=>changePlan(profile.id,e.target.value,`Admin changed to ${e.target.value}`)}
                        disabled={saving===profile.id}
                        style={{fontSize:11,padding:'4px 8px',border:'1px solid #E4E4EC',borderRadius:6,cursor:'pointer',background:'#fff'}}>
                        <option value="hobby">Hobby (Free)</option>
                        <option value="solopreneur">Solopreneur</option>
                        <option value="agency">Agency</option>
                      </select>
                      {saving===profile.id&&<span style={{fontSize:11,color:'#8888AA'}}>⏳</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length===0&&!loading&&<div style={{textAlign:'center',color:'#8888AA',padding:32}}>No users found.</div>}
      </div>
    </div>
  );
}

// ── PLANS EDITOR ──────────────────────────────────────────────────────────────
function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase.from('plans').select('*').order('sort_order');
    setPlans(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (plan) => {
    setSaving(true);
    await supabase.from('plans').update({
      ...plan,
      features: typeof plan.features === 'string' ? plan.features : JSON.stringify(plan.features),
      updated_at: new Date().toISOString(),
    }).eq('id', plan.id);
    supabase.from('admin_logs').insert({ action:'update_plan', target_type:'plan', target_id:plan.id }).then(()=>{}).catch(()=>{});
    await load();
    setEditing(null);
    setSaving(false);
  };

  if (loading) return <div style={{textAlign:'center',color:'#8888AA',padding:40}}>Loading…</div>;

  return (
    <div>
      <h2 style={{margin:'0 0 16px',fontSize:22,fontWeight:800,color:'#0F0E1A'}}>Plan Configuration</h2>
      <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#1D4ED8'}}>
        Changes here update pricing immediately for all users. Module access changes take effect on next login.
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        {plans.map(plan=>{
          const color = PLAN_COLORS[plan.id] || '#64748B';
          const isEdit = editing?.id === plan.id;
          const ep = isEdit ? editing : plan;
          const setEp = (k,v) => setEditing(p=>({...p,[k]:v}));
          return (
            <div key={plan.id} style={{background:'#fff',border:`2px solid ${isEdit?color:color+'30'}`,borderRadius:12,padding:20}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:isEdit?16:0}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:11,padding:'3px 10px',borderRadius:99,background:color+'18',color,fontWeight:700,textTransform:'uppercase'}}>{plan.id}</span>
                  <span style={{fontWeight:700,fontSize:15,color:'#0F0E1A'}}>{plan.name}</span>
                  <span style={{fontSize:22,fontWeight:800,color}}>₹{plan.price_monthly}<span style={{fontSize:12,fontWeight:400,color:'#8888AA'}}>/mo</span></span>
                  {plan.offer_active&&<span style={{fontSize:11,background:'#FAEEDA',color:'#92400E',padding:'2px 8px',borderRadius:99,fontWeight:700}}>🏷 Offer Active</span>}
                </div>
                <div style={{display:'flex',gap:6}}>
                  {!isEdit
                    ? <button onClick={()=>setEditing({...plan, features: typeof plan.features==='string'?plan.features:JSON.stringify(plan.features,null,2)})}
                        style={{fontSize:12,padding:'6px 14px',borderRadius:7,border:`1px solid ${color}`,background:color+'10',color,cursor:'pointer',fontFamily:FONT,fontWeight:600}}>✏️ Edit</button>
                    : <>
                        <button onClick={()=>save(editing)} disabled={saving}
                          style={{fontSize:12,padding:'6px 14px',borderRadius:7,border:'none',background:color,color:'#fff',cursor:'pointer',fontFamily:FONT,fontWeight:700,opacity:saving?0.6:1}}>
                          {saving?'⏳ Saving…':'💾 Save'}
                        </button>
                        <button onClick={()=>setEditing(null)}
                          style={{fontSize:12,padding:'6px 12px',borderRadius:7,border:'1px solid #E4E4EC',background:'#fff',cursor:'pointer',fontFamily:FONT}}>Cancel</button>
                      </>
                  }
                </div>
              </div>

              {isEdit&&(
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div><label style={{fontSize:11,color:'#8888AA',fontWeight:700,display:'block',marginBottom:4}}>Name</label>
                    <input value={ep.name} onChange={e=>setEp('name',e.target.value)} style={{width:'100%',padding:'8px 12px',border:'1.5px solid #E4E4EC',borderRadius:8,fontFamily:FONT,fontSize:13,outline:'none',boxSizing:'border-box'}}/></div>
                  <div><label style={{fontSize:11,color:'#8888AA',fontWeight:700,display:'block',marginBottom:4}}>Tagline</label>
                    <input value={ep.tagline||''} onChange={e=>setEp('tagline',e.target.value)} style={{width:'100%',padding:'8px 12px',border:'1.5px solid #E4E4EC',borderRadius:8,fontFamily:FONT,fontSize:13,outline:'none',boxSizing:'border-box'}}/></div>
                  <div><label style={{fontSize:11,color:'#8888AA',fontWeight:700,display:'block',marginBottom:4}}>Monthly Price (₹)</label>
                    <input type="number" value={ep.price_monthly} onChange={e=>setEp('price_monthly',parseFloat(e.target.value))} style={{width:'100%',padding:'8px 12px',border:'1.5px solid #E4E4EC',borderRadius:8,fontFamily:FONT,fontSize:13,outline:'none',boxSizing:'border-box'}}/></div>
                  <div><label style={{fontSize:11,color:'#8888AA',fontWeight:700,display:'block',marginBottom:4}}>Yearly Price (₹)</label>
                    <input type="number" value={ep.price_yearly||''} onChange={e=>setEp('price_yearly',parseFloat(e.target.value))} style={{width:'100%',padding:'8px 12px',border:'1.5px solid #E4E4EC',borderRadius:8,fontFamily:FONT,fontSize:13,outline:'none',boxSizing:'border-box'}}/></div>
                  <div style={{gridColumn:'1/-1'}}>
                    <label style={{fontSize:11,color:'#8888AA',fontWeight:700,display:'block',marginBottom:4}}>🏷 Offer</label>
                    <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:8}}>
                      <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13}}>
                        <input type="checkbox" checked={ep.offer_active||false} onChange={e=>setEp('offer_active',e.target.checked)} style={{accentColor:color,width:16,height:16}}/>
                        Show offer banner
                      </label>
                      <input type="number" value={ep.offer_price||''} onChange={e=>setEp('offer_price',parseFloat(e.target.value))} placeholder="Offer price (₹)" style={{padding:'6px 10px',border:'1.5px solid #E4E4EC',borderRadius:7,fontFamily:FONT,fontSize:12,outline:'none',width:140}}/>
                    </div>
                    <input value={ep.offer_text||''} onChange={e=>setEp('offer_text',e.target.value)} placeholder="Offer text (e.g. '50% off — Black Friday deal!')" style={{width:'100%',padding:'8px 12px',border:'1.5px solid #E4E4EC',borderRadius:8,fontFamily:FONT,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div style={{gridColumn:'1/-1'}}>
                    <label style={{fontSize:11,color:'#8888AA',fontWeight:700,display:'block',marginBottom:4}}>Features (one per line)</label>
                    <textarea value={typeof ep.features==='string'?ep.features.replace(/[\[\]"]/g,'').replace(/,/g,'\n'):ep.features?.join('\n')||''} onChange={e=>setEp('features',e.target.value.split('\n').filter(Boolean))} rows={5}
                      style={{width:'100%',padding:'8px 12px',border:'1.5px solid #E4E4EC',borderRadius:8,fontFamily:FONT,fontSize:13,outline:'none',resize:'vertical',boxSizing:'border-box'}}/>
                  </div>
                  <div style={{gridColumn:'1/-1',display:'flex',gap:12,alignItems:'center'}}>
                    <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13}}>
                      <input type="checkbox" checked={ep.is_featured||false} onChange={e=>setEp('is_featured',e.target.checked)} style={{accentColor:color}}/>
                      Featured (highlighted on pricing page)
                    </label>
                    <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13}}>
                      <input type="checkbox" checked={ep.is_active} onChange={e=>setEp('is_active',e.target.checked)} style={{accentColor:color}}/>
                      Active (visible to users)
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── PRICING PAGE PREVIEW ───────────────────────────────────────────────────────
function AdminPricingPage() {
  return (
    <div>
      <h2 style={{margin:'0 0 8px',fontSize:22,fontWeight:800,color:'#0F0E1A'}}>Pricing Page Preview</h2>
      <div style={{fontSize:13,color:'#8888AA',marginBottom:16}}>This is how the pricing page looks to users. Edit plans in the Plans tab — changes reflect immediately.</div>
      <div style={{background:'#fff',border:'1px solid #E4E4EC',borderRadius:12,padding:'32px 24px'}}>
        <iframe src="/pricing" style={{width:'100%',height:600,border:'none',borderRadius:8}} title="Pricing preview"/>
      </div>
      <div style={{marginTop:12,fontSize:12,color:'#8888AA'}}>
        Public pricing page is at: <a href="/pricing" target="_blank" style={{color:P}}>/pricing</a>
      </div>
    </div>
  );
}

// ── AUDIT LOGS ─────────────────────────────────────────────────────────────────
function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('admin_logs').select('*,profiles(full_name,email)').order('created_at',{ascending:false}).limit(100)
      .then(({data}) => { setLogs(data||[]); setLoading(false); });
  }, []);

  return (
    <div>
      <h2 style={{margin:'0 0 16px',fontSize:22,fontWeight:800,color:'#0F0E1A'}}>Audit Logs</h2>
      {loading&&<div style={{textAlign:'center',color:'#8888AA',padding:40}}>Loading…</div>}
      <div style={{background:'#fff',border:'1px solid #E4E4EC',borderRadius:12,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{background:'#F6F6F8',borderBottom:'1px solid #E4E4EC'}}>
              {['Time','Admin','Action','Target','Details'].map(h=>(
                <th key={h} style={{padding:'10px 16px',textAlign:'left',fontSize:11,fontWeight:700,color:'#8888AA',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map((log,i)=>(
              <tr key={log.id} style={{borderBottom:'1px solid #F0F0F8',background:i%2?'#FAFAFA':'#fff'}}>
                <td style={{padding:'10px 16px',color:'#8888AA',fontSize:11,whiteSpace:'nowrap'}}>{new Date(log.created_at).toLocaleString('en-IN')}</td>
                <td style={{padding:'10px 16px',fontSize:12}}>{log.profiles?.full_name||log.profiles?.email||'—'}</td>
                <td style={{padding:'10px 16px'}}><span style={{fontSize:11,padding:'2px 8px',borderRadius:99,background:'#EFF6FF',color:'#2563EB',fontWeight:600}}>{log.action}</span></td>
                <td style={{padding:'10px 16px',fontSize:12,color:'#4A4A6A'}}>{log.target_type}: {log.target_id?.slice(0,8)}</td>
                <td style={{padding:'10px 16px',fontSize:11,color:'#8888AA',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{log.details?JSON.stringify(log.details):''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {logs.length===0&&!loading&&<div style={{textAlign:'center',color:'#8888AA',padding:32}}>No admin actions yet.</div>}
      </div>
    </div>
  );
}


// ── AdminFeatureFlags ────────────────────────────────────────────────────────
const ALL_FEATURES = [
  { id:'wiki',         label:'Wiki Pages',           desc:'Unlimited pages & subpages editor',             icon:'📚' },
  { id:'integrations', label:'Integrations Tab',      desc:'Google Sheets, Zoom, Twilio, Resend connections',icon:'🔌' },
  { id:'feed',         label:'Feed & Fellows',        desc:'Accountability partner social feed',             icon:'📢' },
  { id:'business',     label:'Business Module',       desc:'CRM, Invoices, Proposals, WhatsApp',            icon:'🏢' },
  { id:'payments',     label:'Payment Automation',    desc:'Products, Razorpay checkout, automations',      icon:'💳' },
  { id:'meetings',     label:'Meeting Scheduler',     desc:'Booking page, Google Calendar sync',            icon:'📅' },
  { id:'content',      label:'Content Strategy',      desc:'Content pillars, calendar, metrics',            icon:'✍️' },
  { id:'projects',     label:'Projects',              desc:'Kanban project management',                     icon:'📁' },
  { id:'social',       label:'Social Media Hub',      desc:'DM engine, viral links, engagement',            icon:'📱' },
  { id:'vault',        label:'Link Vault',            desc:'Secure link & password manager',                icon:'🔐' },
];

const FEATURE_FLAGS_TABLE = 'admin_feature_flags';

function AdminFeatureFlags() {
  const [flags, setFlags] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load feature flags from a single config row
    supabase.from(FEATURE_FLAGS_TABLE).select('*').eq('id', 'global').maybeSingle()
      .then(({ data }) => {
        if (data?.flags) setFlags(data.flags);
        else {
          // Default: all features enabled
          const defaults = {};
          ALL_FEATURES.forEach(f => { defaults[f.id] = true; });
          setFlags(defaults);
        }
        setLoading(false);
      })
      .catch(() => {
        const defaults = {};
        ALL_FEATURES.forEach(f => { defaults[f.id] = true; });
        setFlags(defaults);
        setLoading(false);
      });
  }, []);

  const toggle = (id) => setFlags(prev => ({ ...prev, [id]: !prev[id] }));

  const saveFlags = async () => {
    setSaving(true);
    await supabase.from(FEATURE_FLAGS_TABLE).upsert({ id: 'global', flags, updated_at: new Date().toISOString() }, { onConflict: 'id' });
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  if (loading) return <div style={{textAlign:'center',padding:40,color:'#8888AA'}}>Loading feature flags…</div>;

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <h2 style={{margin:'0 0 4px',fontSize:22,fontWeight:800,color:'#0F0E1A'}}>⚙️ Feature Flags</h2>
          <div style={{fontSize:13,color:'#8888AA'}}>Toggle features on/off for all users globally. Changes take effect on next app load.</div>
        </div>
        <button onClick={saveFlags} disabled={saving}
          style={{padding:'10px 24px',borderRadius:9,border:'none',background:saving?'#E4E4EC':P,color:saving?'#8888AA':'#fff',fontFamily:FONT,fontWeight:700,fontSize:14,cursor:saving?'not-allowed':'pointer'}}>
          {saving?'Saving…':saved?'✅ Saved!':'💾 Save Changes'}
        </button>
      </div>

      <div style={{background:'#FEF9C3',border:'1px solid #D97706',borderRadius:8,padding:'10px 16px',marginBottom:20,fontSize:12,color:'#92400E'}}>
        ⚠️ Disabling a feature hides it from the navbar and prevents access. User data is NOT deleted — re-enabling restores everything.
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:12}}>
        {ALL_FEATURES.map(f => (
          <div key={f.id} style={{background:'#fff',border:`1.5px solid ${flags[f.id]?P+'40':'#E4E4EC'}`,borderRadius:12,padding:16,display:'flex',alignItems:'center',gap:14,transition:'all .15s'}}>
            <span style={{fontSize:28}}>{f.icon}</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:14,color:'#0F0E1A'}}>{f.label}</div>
              <div style={{fontSize:12,color:'#8888AA',marginTop:2}}>{f.desc}</div>
            </div>
            <label style={{position:'relative',display:'inline-block',width:44,height:24,flexShrink:0,cursor:'pointer'}}>
              <input type="checkbox" checked={!!flags[f.id]} onChange={()=>toggle(f.id)} style={{opacity:0,width:0,height:0}}/>
              <span style={{position:'absolute',inset:0,background:flags[f.id]?P:'#CBD5E1',borderRadius:99,transition:'background .2s'}}/>
              <span style={{position:'absolute',top:2,left:flags[f.id]?22:2,width:20,height:20,background:'#fff',borderRadius:'50%',transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.2)'}}/>
            </label>
          </div>
        ))}
      </div>

      <div style={{marginTop:20,background:'#F0F0F5',borderRadius:8,padding:'12px 16px',fontSize:12,color:'#8888AA'}}>
        <strong>Note:</strong> To make feature flags apply to the navbar, the app reads these flags from Supabase on load. Run this SQL once to create the table:<br/>
        <code style={{fontSize:11,color:'#4A4A6A'}}>CREATE TABLE IF NOT EXISTS public.admin_feature_flags (id TEXT PRIMARY KEY, flags JSONB, updated_at TIMESTAMPTZ DEFAULT NOW()); ALTER TABLE public.admin_feature_flags DISABLE ROW LEVEL SECURITY;</code>
      </div>
    </div>
  );
}
